// 📄 pages/Proyectos/components/ArrangementModal.tsx
// Sub-fase K7: alta/edición de un Communication Arrangement — referencia
// obligatoria a un Communication System ya creado en el pool (K4). Sin
// entornos DEV/QAS/PRD propios (decisión de Fase 0 de K7, ver
// arrangementService.ts).
import React, { useState } from 'react';
import { X, Save, RefreshCw } from 'lucide-react';
import { Arrangement, ArrangementData } from '../../../services/arrangementService.ts';
import { ComunicacionSystem } from '../../../services/comunicacionService.ts';

interface Props {
  arrangement?: Arrangement | null;
  sistemas: ComunicacionSystem[];
  onSave: (data: ArrangementData) => Promise<void>;
  onClose: () => void;
}

export default function ArrangementModal({ arrangement, sistemas, onSave, onClose }: Props) {
  const [nombre, setNombre] = useState(arrangement?.nombre || '');
  const [comunicacionSystemId, setComunicacionSystemId] = useState(arrangement?.comunicacionSystemId || sistemas[0]?.id || '');
  const [scenarioId, setScenarioId] = useState(arrangement?.scenarioId || '');
  const [arrangementId, setArrangementId] = useState(arrangement?.arrangementId || '');
  const [descripcion, setDescripcion] = useState(arrangement?.descripcion || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!comunicacionSystemId) { setError('Seleccioná un Communication System.'); return; }
    setGuardando(true);
    try {
      await onSave({ nombre, comunicacionSystemId, scenarioId, arrangementId, descripcion });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al guardar el Arrangement.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-zinc-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">{arrangement ? 'Editar' : 'Nuevo'} Communication Arrangement</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {sistemas.length === 0 ? (
          <p className="text-xs text-amber-400">
            Todavía no hay ningún Communication System creado. Creá al menos uno en el Repositorio de Comunicación primero.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Nombre</label>
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="ej. Integración BP"
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Communication System</label>
              <select
                value={comunicacionSystemId}
                onChange={(e) => setComunicacionSystemId(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              >
                {sistemas.map((s) => (
                  <option key={s.id} value={s.id}>{s.nombre}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Scenario ID</label>
                <input
                  value={scenarioId}
                  onChange={(e) => setScenarioId(e.target.value)}
                  placeholder="ej. SAP_COM_0008"
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Arrangement ID</label>
                <input
                  value={arrangementId}
                  onChange={(e) => setArrangementId(e.target.value)}
                  placeholder="ej. INTEGRACION_BP"
                  className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Descripción</label>
              <input
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            {error && <p className="text-red-400 text-xs">{error}</p>}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 py-2 rounded-lg text-sm font-semibold transition-all">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={guardando}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 py-2 rounded-lg text-sm font-semibold transition-all"
              >
                {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Guardar</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
